<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { ExtractionPromptSearchEntry } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaContractTypeFilter, {
  contractTypeFilterFromKeys,
} from '@/components/design/domain/contractTypes/GaiaContractTypeFilter.vue';
import ExtractionPromptDrawer from '@/components/contractManagement/ExtractionPromptDrawer.vue';
import ExtractionPromptTable from '@/components/contractManagement/ExtractionPromptTable.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync, usePaginationConfig } from '@/composables/useListQuerySync';
import {
  ExtractionPromptsService,
  type ExtractionPromptFilter,
  type PromptSort,
} from '@/services/contractManagement/extractionPromptsService';
import { useAuthStore } from '@/stores/auth';
import { useContractTypeStore } from '@/stores/contractTypeStore';

/**
 * The organisation's extraction prompts: the questions views ask of every
 * contract. A prompt belongs to any number of contract types, or to none (a
 * standalone prompt); the filter narrows the list to any of those.
 * Everyone can read the list; only an org admin can change it.
 */
const authStore = useAuthStore();
const contractTypeStore = useContractTypeStore();
const message = useGaiaToast();

const canWrite = computed(() => authStore.userPermissions.is_org_admin);

const { currentPage, pageSize, searchQuery, columnFilters, sortKey, sortDirection, sortActivated } =
  useListQuerySync({ defaultSortKey: 'created_at', defaultSortDirection: 'desc' });

/** The header the user sorted by; null while the default order (newest first) applies. */
const sort = computed((): PromptSort | null =>
  sortActivated.value ? { key: sortKey.value, direction: sortDirection.value } : null
);

function handleSort(next: PromptSort | null): void {
  sortActivated.value = next !== null;
  sortKey.value = next?.key ?? 'created_at';
  sortDirection.value = next?.direction ?? 'desc';
  currentPage.value = 1;
}

// ==================== Contract type filter ====================

/** The picked keys, as the URL carries them (`?f_contract_type=`). */
const pickedTypeKeys = computed({
  get: (): string[] => {
    // Only a filter the URL actually carries has the key.
    const keys = columnFilters.value.contract_type as string[] | undefined;
    return keys ?? [];
  },
  set: (keys: string[]) => {
    columnFilters.value = keys.length ? { contract_type: keys } : {};
    currentPage.value = 1;
  },
});

const filter = computed((): ExtractionPromptFilter | undefined =>
  contractTypeFilterFromKeys(pickedTypeKeys.value)
);

// ==================== Rows (server-paged) ====================

const prompts = ref<ExtractionPromptSearchEntry[]>([]);
const total = ref(0);
const loading = ref(false);
let latestRequest = 0;

async function fetchPrompts(): Promise<void> {
  const request = ++latestRequest;
  loading.value = true;
  try {
    const result = await ExtractionPromptsService.search({
      filter: filter.value,
      text: searchQuery.value.trim(),
      offset: (currentPage.value - 1) * pageSize.value,
      limit: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
    });
    if (request !== latestRequest) return;
    prompts.value = result.results;
    total.value = result.result_count;
  } catch (error) {
    if (request !== latestRequest) return;
    message.error(error instanceof Error ? error.message : 'Failed to load prompts');
    prompts.value = [];
    total.value = 0;
  } finally {
    if (request === latestRequest) loading.value = false;
  }
}

const pagination = usePaginationConfig(currentPage, pageSize, total);

// usePaginationConfig only updates the refs; the fetch follows them.
watch([currentPage, pageSize, pickedTypeKeys, sortKey, sortDirection], () => {
  void fetchPrompts();
});

function applySearch(): void {
  if (currentPage.value === 1) {
    void fetchPrompts();
  } else {
    currentPage.value = 1;
  }
}

/** The organisation has no prompts at all, rather than none matching. */
const showEmpty = computed(
  () => !loading.value && total.value === 0 && !searchQuery.value.trim() && !filter.value
);

// ==================== Adding a prompt ====================

const showPromptDrawer = ref(false);
/** The prompt the panel edits; null while it creates one. */
const editedPrompt = ref<ExtractionPromptSearchEntry | null>(null);

function openAddPrompt(): void {
  editedPrompt.value = null;
  showPromptDrawer.value = true;
}

function openEditPrompt(prompt: ExtractionPromptSearchEntry): void {
  editedPrompt.value = prompt;
  showPromptDrawer.value = true;
}

/** A new prompt starts in the picked contract types. */
const newPromptTypeUids = computed((): string[] => filter.value?.contractTypeUids ?? []);

onMounted(() => {
  void contractTypeStore.ensureLoaded();
  void fetchPrompts();
});
</script>

<template>
  <!-- Adding a prompt opens beside the list rather than over it: the layout's drawer squeezes
       the page, so the prompts stay in view. -->
  <Layout
    :content-max-width="gaiaContentWidth.table"
    :drawer-open="showPromptDrawer"
    :drawer-width="580"
  >
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.prompts }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="18">
      <n-flex align="flex-start" :size="[24, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            {{ navLabels.prompts }}
          </gaia-header>
        </n-flex>
        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            placeholder="Search prompts"
            :debounce="500"
            style="width: 220px"
            @search="applySearch"
          />
          <gaia-contract-type-filter v-model="pickedTypeKeys" without-option />
          <gaia-add-button
            v-if="canWrite"
            subject="prompt"
            variant="primary"
            @click="openAddPrompt"
          />
        </n-flex>
      </n-flex>

      <gaia-empty-state v-if="showEmpty" dashed title="No prompts yet">
        Add the questions your views should ask of every contract.
        <template v-if="canWrite" #actions>
          <gaia-add-button subject="prompt" :icon="false" @click="openAddPrompt" />
        </template>
      </gaia-empty-state>

      <extraction-prompt-table
        v-else
        :prompts="prompts"
        :loading="loading"
        :pagination="pagination"
        :can-write="canWrite"
        :truncate-prompt="showPromptDrawer"
        sortable
        :sort="sort"
        @changed="fetchPrompts"
        @edit="openEditPrompt"
        @sort="handleSort"
      >
        <template #empty>
          <n-empty v-if="!loading" description="No prompt matches your search or filter" />
        </template>
      </extraction-prompt-table>
    </n-flex>

    <template #drawer>
      <extraction-prompt-drawer
        v-model:show="showPromptDrawer"
        inline
        :prompt="editedPrompt"
        :default-contract-type-uids="newPromptTypeUids"
        @saved="fetchPrompts"
      />
    </template>
  </Layout>
</template>
